import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, X, Truck, User, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const OpsEquipes = () => {
  const [equipes, setEquipes] = useState([]);
  const [dispos, setDispos] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [formOuvert, setFormOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(null);
  const [erreur, setErreur] = useState('');
  const [form, setForm] = useState({ nom: '', chef_id: '', equipiers_ids: [], camion_nom: '', camion_immat: '' });

  const charger = async () => {
    try {
      const [rEq, rDispo] = await Promise.all([
        axios.get(`${API}/api/equipes`),
        axios.get(`${API}/api/equipes/disponibles`)
      ]);
      setEquipes(rEq.data.data || []);
      setDispos(rDispo.data.data || []);
    } catch (e) {
      setErreur('Impossible de charger les équipes');
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const fermer = () => {
    setFormOuvert(false);
    setEnEdition(null);
    setErreur('');
    setForm({ nom: '', chef_id: '', equipiers_ids: [], camion_nom: '', camion_immat: '' });
  };

  const ouvrirEdition = (eq) => {
    setErreur('');
    setEnEdition(eq.id);
    setForm({
      nom: eq.nom || '',
      chef_id: eq.chef_id || '',
      equipiers_ids: eq.equipiers_ids || [],
      camion_nom: eq.camion_nom || '',
      camion_immat: eq.camion_immat || ''
    });
    setFormOuvert(true);
  };

  // Candidats = disponibles + membres actuels de l'équipe en cours d'édition
  const candidats = () => {
    if (!enEdition) return dispos;
    const eq = equipes.find((e) => e.id === enEdition);
    const membres = [];
    if (eq?.chef) membres.push(eq.chef);
    (eq?.equipiers || []).forEach((m) => membres.push(m));
    const vus = new Set(dispos.map((d) => d.id));
    return [...dispos, ...membres.filter((m) => !vus.has(m.id))];
  };

  const basculerEquipier = (id) => {
    const liste = form.equipiers_ids.includes(id)
      ? form.equipiers_ids.filter((x) => x !== id)
      : [...form.equipiers_ids, id];
    setForm({ ...form, equipiers_ids: liste });
  };

  const enregistrer = async () => {
    setErreur('');
    if (!form.nom.trim()) { setErreur("Le nom de l'équipe est obligatoire"); return; }
    if (!form.chef_id) { setErreur("Un chef d'équipe est obligatoire"); return; }
    try {
      if (enEdition) {
        await axios.put(`${API}/api/equipes/${enEdition}`, form);
      } else {
        await axios.post(`${API}/api/equipes`, form);
      }
      fermer();
      charger();
    } catch (e) {
      setErreur(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const supprimer = async () => {
    if (!enEdition) return;
    try {
      await axios.delete(`${API}/api/equipes/${enEdition}`);
      fermer();
      charger();
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const champ = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', marginBottom: '12px' };

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button onClick={() => { setErreur(''); setFormOuvert(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#10B981', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '14px', cursor: 'pointer' }}>
          <Plus size={16} /> Nouvelle équipe
        </button>
      </div>

      {erreur && !formOuvert && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{erreur}</div>
      )}

      {chargement ? (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#6B7280' }}>Chargement…</div>
      ) : equipes.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', textAlign: 'center', color: '#6B7280' }}>Aucune équipe créée</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {equipes.map((eq) => (
            <div key={eq.id} onClick={() => ouvrirEdition(eq)}
              style={{ background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer' }}>
              <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '14px' }}>{eq.nom}</div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <User size={15} color="#10B981" />
                <span style={{ fontSize: '14px' }}>{eq.chef ? `${eq.chef.prenom} ${eq.chef.nom}` : '—'}</span>
                <span style={{ fontSize: '11px', background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: '999px' }}>Chef</span>
              </div>

              {(eq.equipiers || []).map((m) => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <User size={15} color="#9CA3AF" />
                  <span style={{ fontSize: '14px', color: '#374151' }}>{m.prenom} {m.nom}</span>
                </div>
              ))}

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #F3F4F6' }}>
                <Truck size={15} color="#6B7280" />
                <span style={{ fontSize: '13px', color: '#6B7280' }}>
                  {eq.camion_nom || eq.camion_immat ? `${eq.camion_nom || ''} ${eq.camion_immat ? '· ' + eq.camion_immat : ''}` : 'Aucun camion'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOuvert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>{enEdition ? "Modifier l'équipe" : 'Nouvelle équipe'}</h3>
              <X size={20} style={{ cursor: 'pointer', color: '#6B7280' }} onClick={fermer} />
            </div>

            {erreur && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{erreur}</div>}

            <input style={champ} placeholder="Nom de l'équipe *" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />

            <label style={{ fontSize: '13px', color: '#6B7280' }}>Chef d'équipe *</label>
            <select style={champ} value={form.chef_id} onChange={(e) => setForm({ ...form, chef_id: e.target.value, equipiers_ids: form.equipiers_ids.filter((x) => x !== e.target.value) })}>
              <option value="">Sélectionner…</option>
              {candidats().map((c) => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
            </select>

            <label style={{ fontSize: '13px', color: '#6B7280', display: 'block', marginBottom: '6px' }}>Équipiers</label>
            <div style={{ border: '1px solid #D1D5DB', borderRadius: '8px', padding: '8px', marginBottom: '12px', maxHeight: '160px', overflowY: 'auto' }}>
              {candidats().filter((c) => c.id !== form.chef_id).length === 0 ? (
                <div style={{ color: '#9CA3AF', fontSize: '13px', padding: '8px' }}>Aucun employé disponible</div>
              ) : candidats().filter((c) => c.id !== form.chef_id).map((c) => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.equipiers_ids.includes(c.id)} onChange={() => basculerEquipier(c.id)} />
                  <span style={{ fontSize: '14px' }}>{c.prenom} {c.nom}</span>
                </label>
              ))}
            </div>

            <input style={champ} placeholder="Nom du camion (ex. Camion 1)" value={form.camion_nom} onChange={(e) => setForm({ ...form, camion_nom: e.target.value })} />
            <input style={champ} placeholder="Immatriculation" value={form.camion_immat} onChange={(e) => setForm({ ...form, camion_immat: e.target.value })} />

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={fermer} style={{ flex: 1, padding: '10px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
              <button onClick={enregistrer} style={{ flex: 1, padding: '10px', border: 'none', background: '#10B981', color: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Enregistrer</button>
            </div>

            {enEdition && (
              <button onClick={supprimer}
                style={{ width: '100%', marginTop: '10px', padding: '9px', border: '1px solid #FCA5A5', background: '#fff', color: '#B91C1C', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '13px' }}>
                <Trash2 size={14} /> Supprimer l'équipe
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OpsEquipes;
